/**
 * Small tree helpers shared by the code block and the rehype plugins that give react-markdown's
 * output the DOM `markdown-content.css` styles.
 */
import type { Element, ElementContent, Nodes, Root } from 'hast'
import { visit } from 'unist-util-visit'

export type { Element as HastElement, Nodes as HastNodes, Root as HastRoot } from 'hast'

export function classNames(node: Element): Array<string> {
  const value = node.properties.className
  if (Array.isArray(value)) return value.map(String)
  return typeof value === 'string' ? value.split(/\s+/) : []
}

export function extractText(node: Nodes | undefined): string {
  if (!node) return ''
  if (node.type === 'text') return node.value
  return 'children' in node ? node.children.map(extractText).join('') : ''
}

/** True when `node` is an element with one of `tags`. */
export const isElement = (node: Nodes, ...tags: Array<string>): node is Element =>
  node.type === 'element' && tags.includes(node.tagName)

/**
 * Splits highlighted nodes at newlines, re-opening each wrapping span on the next line, so every
 * line can be its own row beside a gutter. A span that crosses a line break (a block comment) ends
 * up as one span per line, each with the same classes.
 */
export function toLines(nodes: Array<ElementContent>): Array<Array<ElementContent>> {
  const lines: Array<Array<ElementContent>> = [[]]
  for (const node of nodes) {
    if (node.type === 'text') {
      node.value.split('\n').forEach((part, index) => {
        if (index > 0) lines.push([])
        if (part) lines[lines.length - 1].push({ type: 'text', value: part })
      })
    } else if (node.type === 'element') {
      toLines(node.children).forEach((part, index) => {
        if (index > 0) lines.push([])
        if (part.length) lines[lines.length - 1].push({ ...node, children: part })
      })
    }
  }
  return lines
}

function element(tagName: string, children: Array<ElementContent>): Element {
  return { type: 'element', tagName, properties: {}, children }
}

const isList = (node: Nodes) => isElement(node, 'ul', 'ol')

/**
 * A column is prose or UI as a whole, not cell by cell. Deciding per cell on a character count puts
 * two type families in one column, so a short row and a long row are set differently.
 */
const PROSE_COLUMN_AVERAGE = 56

function shapeTable(table: Element) {
  const children = table.children
  const head = children.find((child) => isElement(child, 'thead'))
  const body = children.find((child) => isElement(child, 'tbody'))
  const rows = (body?.children ?? []).filter((row) => isElement(row, 'tr'))
  const cellsOf = (row: Element) => row.children.filter((cell) => isElement(cell, 'td'))

  const headCells = (head?.children ?? []).flatMap((row) =>
    isElement(row, 'tr') ? row.children.filter((cell) => isElement(cell, 'th')) : [],
  )
  // A table whose header cells are all empty is a definition list in table syntax. Emitting the empty
  // row gives it a heavy rule with nothing above it, so the head goes and the rows carry themselves.
  if (head && headCells.every((cell) => !extractText(cell).trim())) {
    table.children = children.filter((child) => child !== head)
  }

  for (let column = 0; column < headCells.length; column++) {
    const lengths = rows.map((row) => extractText(cellsOf(row)[column]).length)
    const average = lengths.reduce((sum, length) => sum + length, 0) / (lengths.length || 1)
    if (average <= PROSE_COLUMN_AVERAGE) continue
    for (const row of rows) {
      const cell = cellsOf(row)[column]
      if (cell) cell.properties = { ...cell.properties, dataCol: 'prose' }
    }
  }
}

/** Marks the table's columns as UI or prose, and drops an empty header row. */
export function rehypeTables() {
  return (tree: Root) => {
    visit(tree, 'element', (node) => {
      if (node.tagName === 'table') shapeTable(node)
    })
  }
}

/**
 * Reshapes a GFM task item into the shape Tiptap's task items already have,
 * `li[data-checked] > label > input` beside a `div` of content, so one set of rules styles the
 * read and the edit surface and a done item can dim its own text without dimming a nested list.
 */
function shapeTaskItem(item: Element) {
  const lead = item.children.find((child) => isElement(child, 'p'))
  // Tight items hold the checkbox directly, loose ones inside their first paragraph.
  const parent = item.children.some((child) => isElement(child, 'input')) ? item : lead
  const input = parent?.children.find((child) => isElement(child, 'input'))
  if (!parent || !input) return

  parent.children = parent.children.filter((child) => child !== input)
  const first = parent.children[0]
  if (first?.type === 'text') first.value = first.value.replace(/^\s+/, '')

  const checked = Boolean(input.properties?.checked)
  input.properties = { ...input.properties, ariaLabel: checked ? 'Done' : 'Not done' }

  // A tight item is bare text and maybe a nested list. The text gets a paragraph, so the done style
  // has one block to dim and the list stays outside it.
  let content = item.children
  if (!content.some((child) => isElement(child, 'p'))) {
    const listAt = content.findIndex(isList)
    const text = listAt === -1 ? content : content.slice(0, listAt)
    const lists = listAt === -1 ? [] : content.slice(listAt)
    content = [...(text.some((node) => extractText(node).trim()) ? [element('p', text)] : []), ...lists]
  }

  item.properties = { dataChecked: String(checked) }
  item.children = [element('label', [input]), element('div', content)]
}

/** Gives every GFM task item the label-and-content shape described above. */
export function rehypeTaskItems() {
  return (tree: Root) => {
    visit(tree, 'element', (node) => {
      if (node.tagName === 'li' && classNames(node).includes('task-list-item')) shapeTaskItem(node)
    })
  }
}
